import * as Sentry from '@sentry/browser';
import type { Logout } from 'flowhawk-core';
import { selectProfileName } from 'flowhawk-core';
import type { Reducer } from '@reduxjs/toolkit';

import { keys, values } from '../Utils/Object';
import type {
  ChatroomInfo,
  ChatroomInfoMap,
  ChatroomMessage,
  ChatroomMessageMap,
  ChatroomMemberMap,
  ChatroomType,
  MessageId,
  LastMessage,
  ChatSocketStatus,
  ChatSocketError,
  ReadStatusMap,
  MessageReadStatus,
  MessageSendStatus,
  MessageType,
  SearchResultMap,
  SearchResult,
  ChatroomFile,
  ChatroomFileMap,
  ContactsMap,
  UserProfile,
  ChatroomMemberInfo,
  PinnedMessageMap,
  PinnedMessageMetadata,
} from '../Utils/types';
import { FileMessageTypes } from '../Utils/types';
import { getBadgeCount } from '../Utils/sortChatrooms';
import type { ResetData, ResetError } from './ApplicationActions';
import { resetReducerErrors, getReducerError, type ReducerError } from '../Utils/reducerError';
import type { SaveChatSuccess } from './ChatEditActions';
import hasValue from '../Utils/hasValue';
import { NotificationSoundMap } from '../Themes/Sounds';
import createUuid from '../Utils/createUuid';
import devLog from '../Utils/logger';

export const CHAT_PAGE_SIZE = 50;

export type ChatListState = {
  activeChatId?: number;

  order: Array<number>;
  chats: ChatroomInfoMap;
  isTyping: boolean;

  filter: string;
  offerFilter: string;
  organizationFilter: string;
  peopleFilter: string;

  socketStatus: ChatSocketStatus;
  socketError?: ChatSocketError;
  // Timestamp of the most recent socket reconnect. Rooms whose cached messagesRefreshedTime
  // predates this are treated as stale, since updates may have been missed while disconnected.
  lastReconnectedTime?: string;

  searchResults?: SearchResultMap; // null -> no search results found, [] -> search has not occurred
  selectedResult?: SearchResult; // search result that user clicks on
  scrolledToSelectedResult: boolean;
  selectedScrollTargetId?: number; // message ID to scroll to
  scrolledToScrollTarget: boolean;
  deepSearchFilter: string; // This is used so that the highlighted text that matches a search result only changes when the user
  // performs a deep search and doesn't change as the user types in the search bar.
  reducerErrors: Array<ReducerError>;
};

const initialState: ChatListState = {
  activeChatId: undefined,
  order: [],
  chats: {} as ChatroomInfoMap,
  isTyping: false,

  filter: '',
  offerFilter: '',
  organizationFilter: '',
  peopleFilter: '',

  socketStatus: 'unknown',
  socketError: undefined,
  lastReconnectedTime: undefined,

  searchResults: {},
  selectedResult: undefined,
  scrolledToSelectedResult: false,
  selectedScrollTargetId: undefined,
  scrolledToScrollTarget: false,
  deepSearchFilter: '',
  reducerErrors: [],
};

export type ResetChatList = { type: 'ChatList/RESET_DATA' };
export type Search = { type: 'ChatList/SEARCH'; searchText: string };
export type SetSearchResults = { type: 'ChatList/SEARCH_RESULTS'; searchResults?: SearchResultMap };
export type SetSelectedResult = { type: 'ChatList/SET_SELECTED_RESULT'; selectedResult: SearchResult };
export type SetSelectedScrollTarget = { type: 'ChatList/SET_SELECTED_SCROLL_TARGET'; selectedScrollTargetId?: number };
export type SetScrolledToScrollTarget = {
  type: 'ChatList/SET_SCROLLED_TO_SCROLL_TARGET';
  scrolledToScrollTarget: boolean;
};
export type AddedToChatroom = {
  type: 'ChatList/ADDED_TO_CHATROOM';
  roomId: number;
  isPushNotification: boolean;
  name: string;
  description: string;
  chatroomType: ChatroomType;
  managed: boolean;
  creatorId: number;
  memberCount: number;
  totalMessages?: number;
  lastMessage?: LastMessage;
  lastUpdateTime?: string;
  lastActiveTime?: string;
  donorId?: number;
  organId?: number;
  followerId?: number;
  createDate: string;
  caseName?: string;
  followerName?: string;
};
export type RemovedFromChatroom = { type: 'ChatList/REMOVED_FROM_CHATROOM'; roomId: number };
export type DeletedChatroom = { type: 'ChatList/DELETED_CHATROOM'; roomId: number };
export type MemberRemoved = {
  type: 'ChatList/MEMBER_REMOVED';
  chatId: number;
  memberId: number;
  memberCount: number;
  leftChat: boolean;
};
export type RemoveMemberFromRoom = {
  type: 'ChatList/REMOVE_MEMBER_FROM_ROOM';
  chatId: number;
  memberId: number;
};
export type ReceiveChatroomMembers = {
  type: 'ChatList/RECEIVE_ROOM_MEMBERS';
  id: number;
  memberId: number;
  members: ChatroomMemberMap;
  memberOrder: Array<number>;
  hint: 'replace' | 'extend';
};

export type ReceiveBulkChatroomMembers = {
  type: 'ChatList/BULK_RECEIVE_MEMBERS';
  memberId: number;
  rooms: ChatroomMemberInfo[];
};

export type LoadChatMembers = {
  type: 'ChatList/LOAD_MEMBERS';
  chatId: number;
};

export type LoadPinnedMessages = {
  type: 'ChatList/LOAD_PINNED_MESSAGES';
  chatId: number;
};

export type LoadMessages = {
  type: 'ChatList/LOAD_MESSAGES';
  chatId: number;
  memberId: number;
  resetMessages: boolean;
  numberMessages: number; // number of messages to load
};

// Forces a full reload of a room's most-recently-loaded messages (offset 0, hint 'replace')
//  to refresh presigned media URLs, without pulling in the unread-backlog offset that
//  ChatList/LOAD_MESSAGES uses for a true cold start.
export type RefreshStaleMessages = {
  type: 'ChatList/REFRESH_STALE_MESSAGES';
  chatId: number;
  memberId: number;
  numberMessages: number;
};

export type PartialChatroomInfo = {
  id: number;
  name: string;
  description: string;
  chatroomType: ChatroomType;
  managed: boolean;
  doNotDisturb: boolean;
  createDate: string;
  creatorId?: number;
  memberCount: number;
  managerIds?: number[];
  pinNumber?: number;
  totalMessages: number;
  sentCount: number;
  readCount: number;
  clearedCount: number;
  lastMessage?: LastMessage;
  lastUpdateTime?: string;
  lastActiveTime?: string;
  donorId?: number;
  organId?: number;
  organType?: string;
  targetOrgId?: number;
  donorClosed: boolean;
  followerId?: number;
  caseName?: string;
  followerName?: string;
  roomIcon?: string;
};

export type ReceiveChatroomInfo = {
  type: 'ChatList/RECEIVE_ROOM_INFO';
  id: number;
  name: string;
  description: string;
  chatroomType: ChatroomType;
  managed: boolean;
  creatorId: number;
  memberCount: number;
  pinNumber?: number;
  totalMessages: number;
  sentCount: number;
  readCount: number;
  clearedCount: number;
  lastMessage?: LastMessage;
  lastUpdateTime?: string;
  lastActiveTime?: string;
  donorId?: number;
  organId?: number;
  targetOrgId?: number;
  followerId?: number;
  createDate: string;
  caseName?: string;
  followerName?: string;
  roomIcon?: string;
};

export type UpdateChatroomInfo = {
  type: 'ChatList/UPDATE_CHATROOM_INFO';
  id: number;
  name?: string;
  description: string;
  managed: boolean;
  lastUpdateTime?: string;
};

export type UpdateRoomIcon = {
  type: 'ChatList/UPDATE_ROOM_ICON';
  id: number;
  roomIcon: string | undefined;
};

export type ReceiveRoomIconUpdate = {
  type: 'ChatList/RECEIVE_ROOM_ICON_UPDATE';
  id: number;
  roomIcon: string | undefined;
};

export type ReceiveChatroomMessagesInfo = Omit<ReceiveChatroomMessages, 'type'>;

export type ReceiveChatroomMessages = {
  type: 'ChatList/RECEIVE_ROOM_MESSAGES';
  id: number;
  memberId: number;
  messages: ChatroomMessageMap;
  messageOrder: Array<MessageId>;
  hint: 'end' | 'start' | 'replace';
  lastMessage?: LastMessage;
  lastActiveTime?: string;
  // Set only when this batch represents a full reload (hint 'replace') of the room's messages,
  //  e.g. from ChatList/LOAD_MESSAGES or ChatList/REFRESH_STALE_MESSAGES. Stamped onto
  //  ChatroomInfo.messagesRefreshedTime so future room opens can tell how fresh the cached
  //  presigned media URLs are. Left undefined for incremental/pagination fetches.
  refreshedTime?: string;
  isOldPage: boolean;
};

export type ReceiveNewMessage = {
  type: 'ChatList/RECEIVE_NEW_MESSAGE';
  id: number;
  memberId: number;
  message: ChatroomMessage;
  messageId: MessageId;
  hint: 'end' | 'start' | 'replace';
  lastMessage?: LastMessage;
  lastActiveTime?: string;
  sentCountModifier: number;
};

export type AttemptReceiveNewMessage = {
  type: 'ChatList/ATTEMPT_RECEIVE_NEW_MESSAGE';
  id: number;
  memberId: number;
  message: ChatroomMessage;
  messageId: MessageId;
  hint: 'end' | 'start' | 'replace';
  lastMessage?: LastMessage;
  lastActiveTime?: string;
  sentCountModifier: number;
};

export type OpenChatroom = {
  type: 'ChatList/OPEN_CHATROOM';
  chatId: number;
};

export type CloseChatroom = {
  type: 'ChatList/CLOSE_CHATROOM';
  chatId: number;
};

export type RemoveChatroom = {
  type: 'ChatList/REMOVE';
  chatId: number;
};

export type ClearChatroomNotifications = {
  type: 'ChatList/CLEAR_NOTIFICATIONS';
  chatId: number;
};

export type SetClearedNotificationCount = {
  type: 'ChatList/SET_CLEARED_COUNT';
  chatId: number;
  newCount?: number;
};

export type ReceiveBulkChatroomInfo = {
  type: 'ChatList/BULK_RECEIVE_ROOMS';
  chatrooms: Array<PartialChatroomInfo>;
  updateOrder: boolean;
};

export type MessageSendSuccess = {
  type: 'ChatList/MESSAGE_SUCCESS';
  chatId: number;
  memberId: number;
  tmpId: string;
  newId: number;
  updateData: {
    status?: MessageSendStatus;
    sentTime: string;
    messageType: MessageType;
    textContent: string;
    fileName?: string;
    displayName?: string;
  };
};

export type LoadChatFiles = {
  type: 'ChatList/LOAD_FILES';
  chatId: number;
  memberId: number;
};

export type ReceiveChatroomFiles = {
  type: 'ChatList/RECEIVE_ROOM_FILES';
  id: number;
  fileMap: ChatroomFileMap;
  allFiles: Array<MessageId>;
  myFiles: Array<MessageId>;
};

export type SetSelectedFile = {
  type: 'ChatList/SET_SELECTED_FILE';
  id: number;
  selectedFile: ChatroomFile;
};

export type SetMessageSendStatus = {
  type: 'ChatList/MESSAGE_SEND_STATUS';
  chatId: number;
  messageId: MessageId;
  status?: MessageSendStatus;
};

export type LoadMessageStatuses = {
  type: 'ChatList/LOAD_MSG_STATUSES';
  chatId: number;
  memberId: number;
  messageIds: Array<number>;
};

export type BulkUpdateMessageStatus = {
  type: 'ChatList/BULK_UPDATE_MESSAGE_STATUS';

  memberId: number; // id of the current user
  chatId: number;
  statuses: {
    [key: string]: ReadStatusMap;
  };
  mode: 'extend' | 'replace';
  newStatusCount: number;
  updatedStatusCount: number;
};

export type MarkMessagesRead = {
  type: 'ChatList/MARK_MESSAGES_READ';
  chatId: number;
  memberId: number;
  untilId: MessageId;
};

export type TriggerChatSocket = {
  type: 'ChatList/SOCKET';
};

export type SetChatSocketStatus = {
  type: 'ChatList/SOCKET_STATUS';
  status: ChatSocketStatus;
  error?: ChatSocketError;
};

export type SetLastReconnectedTime = {
  type: 'ChatList/SET_LAST_RECONNECTED_TIME';
  time: string;
};

export type SetOffset = {
  type: 'ChatList/SET_PAGE_OFFSET';
  chatId: number;
  memberId: number;
  offset: number;
  numberMessages?: number;
};

export type SetScrolledToResult = {
  type: 'ChatList/SET_SCROLLED_TO_SEARCH_RESULT';
  scrolledToSelectedResult: boolean;
};

export type UpdateDonorId = {
  type: 'ChatList/UPDATE_DONOR_ID';
  chatId: number;
  donorId: number;
};

export type UpdateOrganId = {
  type: 'ChatList/UPDATE_ORGAN_ID';
  chatId: number;
  organId: number;
};

export type FilterChatrooms = { type: 'ChatList/FILTER'; filter: string };
export type FilterOffers = { type: 'ChatList/FILTER_OFFER'; offerFilter: string };
export type FilterOrganizations = { type: 'ChatList/FILTER_ORGANIZATION'; organizationFilter: string };
export type FilterPeople = { type: 'ChatList/FILTER_PEOPLE'; peopleFilter: string };
export type ResetPeopleFilter = { type: 'ChatList/RESET_PEOPLE_FILTER' };
export type ResetFilters = { type: 'ChatList/RESET_FILTERS' };

export type AcknowledgeMessage = { type: 'ChatList/ACK_MESSAGE'; chatId: number; messageId: number; symbol: string };
export type ReceiveAcknowledge = {
  type: 'ChatList/RECEIVE_ACK';
  chatId: number;
  messageId: number;
  memberId: number;
  symbol: string;
};

export type StartTyping = { type: 'ChatList/START_TYPING'; chatId: number };
export type StopTyping = { type: 'ChatList/STOP_TYPING'; chatId: number };
export type MemberStartedTyping = { type: 'ChatList/MEMBER_STARTED_TYPING'; chatId: number; memberId: number };
export type MemberStoppedTyping = { type: 'ChatList/MEMBER_STOPPED_TYPING'; chatId: number; memberId: number };

export type PinChatroom = { type: 'ChatList/PIN_CHATROOM'; chatId: number };
export type PinnedChatroom = { type: 'ChatList/PINNED_CHATROOM'; chatId: number; pinNumber: number };
export type UnpinChatroom = { type: 'ChatList/UNPIN_CHATROOM'; chatId: number };
export type UnpinnedChatroom = { type: 'ChatList/UNPINNED_CHATROOM'; chatId: number };
export type UpdatePinnedChatrooms = {
  type: 'ChatList/UPDATE_PINNED_CHATROOMS';
  pinnedChatrooms: { [key: string]: number };
};

export type PinMessage = {
  type: 'ChatList/PIN_MESSAGE';
  chatId: number;
  messageId: number;
};
export type PinnedMessage = {
  type: 'ChatList/PINNED_MESSAGE';
  chatId: number;
  messageId: number;
  pinOrder: number;
};
export type UnpinMessage = {
  type: 'ChatList/UNPIN_MESSAGE';
  chatId: number;
  messageId: number;
};
export type UnpinnedMessage = {
  type: 'ChatList/UNPINNED_MESSAGE';
  chatId: number;
  messageId: number;
};
export type ReceivePinnedMessages = {
  type: 'ChatList/RECEIVE_PINNED_MESSAGES';
  chatId: number;
  pinnedMessagesMetadata: PinnedMessageMetadata[];
};

export type RedactMessage = {
  type: 'ChatList/REDACT_MESSAGE';
  chatId: number;
  messageId: number;
};
export type RedactMessageSuccess = {
  type: 'ChatList/REDACT_SUCCESS';
  chatId: number;
  messageId: number;
  redactTime: string;
};
export type UnredactMessage = {
  type: 'ChatList/UNREDACT_MESSAGE';
  chatId: number;
  messageId: number;
};
export type UnredactMessageSuccess = {
  type: 'ChatList/UNREDACT_SUCCESS';
  chatId: number;
  messageId: number;
};
export type EditMessage = {
  type: 'ChatList/EDIT_MESSAGE';
  chatId: number;
  messageId: number;
  textContent: string;
};
export type EditMessageSuccess = {
  type: 'ChatList/EDIT_SUCCESS';
  chatId: number;
  messageId: number;
  textContent: string;
  editTime: string;
  editedBy: number;
};

export type DeleteFileMessage = {
  type: 'ChatList/DELETE_FILE_MESSAGE';
  chatId: number;
  messageId: number;
};

export type DeleteFileMessageSuccess = {
  type: 'ChatList/DELETE_FILE_MESSAGE_SUCCESS';
  chatId: number;
  messageId: number;
};

export type ReceiveRoomDoNotDisturb = {
  type: 'ChatList/RECEIVE_ROOM_DO_NOT_DISTURB';
  chatId: number;
  doNotDisturb: boolean;
};

export type ReceiveAddChatroomManager = {
  type: 'ChatList/RECEIVE_ADD_CHATROOM_MANAGER';
  chatId: number;
  userId: number;
};

export type ReceiveRemoveChatroomManager = {
  type: 'ChatList/RECEIVE_REMOVE_CHATROOM_MANAGER';
  chatId: number;
  userId: number;
};

export type ReceiveChatroomManagers = {
  type: 'ChatList/RECEIVE_CHATROOM_MANAGERS';
  chatId: number;
  managerIds: number[];
};

export type ChatListAction =
  | ResetChatList
  | Search
  | SetSearchResults
  | SetSelectedResult
  | SetSelectedScrollTarget
  | SetScrolledToScrollTarget
  | SetScrolledToResult
  | AddedToChatroom
  | RemovedFromChatroom
  | MemberRemoved
  | RemoveMemberFromRoom
  | SetOffset
  | ReceiveChatroomInfo
  | ReceiveBulkChatroomInfo
  | UpdateChatroomInfo
  | OpenChatroom
  | CloseChatroom
  | RemoveChatroom
  | ClearChatroomNotifications
  | ReceiveChatroomMembers
  | ReceiveBulkChatroomMembers
  | LoadChatMembers
  | LoadPinnedMessages
  | LoadMessages
  | RefreshStaleMessages
  | ReceiveChatroomMessages
  | ReceiveNewMessage
  | AttemptReceiveNewMessage
  | MessageSendSuccess
  | SetMessageSendStatus
  | LoadChatFiles
  | ReceiveChatroomFiles
  | SetSelectedFile
  | SetClearedNotificationCount
  | BulkUpdateMessageStatus
  | LoadMessageStatuses
  | MarkMessagesRead
  | UpdateDonorId
  | UpdateOrganId
  | FilterChatrooms
  | FilterOffers
  | FilterOrganizations
  | FilterPeople
  | ResetPeopleFilter
  | ResetFilters
  | AcknowledgeMessage
  | ReceiveAcknowledge
  | StartTyping
  | StopTyping
  | MemberStartedTyping
  | MemberStoppedTyping
  | PinChatroom
  | PinnedChatroom
  | UnpinChatroom
  | UnpinnedChatroom
  | UpdatePinnedChatrooms
  | PinMessage
  | PinnedMessage
  | UnpinMessage
  | UnpinnedMessage
  | ReceivePinnedMessages
  | RedactMessage
  | RedactMessageSuccess
  | UnredactMessage
  | UnredactMessageSuccess
  | EditMessage
  | EditMessageSuccess
  | DeleteFileMessage
  | DeleteFileMessageSuccess
  | TriggerChatSocket
  | SetChatSocketStatus
  | SetLastReconnectedTime
  | SaveChatSuccess
  | UpdateRoomIcon
  | ReceiveRoomIconUpdate
  | Logout
  | ResetData
  | ResetError
  | ReceiveRoomDoNotDisturb
  | ReceiveAddChatroomManager
  | ReceiveRemoveChatroomManager
  | ReceiveChatroomManagers;

export const resetChatList = (): ResetChatList => ({
  type: 'ChatList/RESET_DATA',
});

export const search = (searchText: string): Search => ({
  type: 'ChatList/SEARCH',
  searchText,
});

export const setSearchResults = (searchResults?: SearchResultMap): SetSearchResults => ({
  type: 'ChatList/SEARCH_RESULTS',
  searchResults,
});

export const setSelectedResult = (selectedResult: SearchResult): SetSelectedResult => ({
  type: 'ChatList/SET_SELECTED_RESULT',
  selectedResult,
});

export const setSelectedScrollTarget = (selectedScrollTargetId?: number): SetSelectedScrollTarget => ({
  type: 'ChatList/SET_SELECTED_SCROLL_TARGET',
  selectedScrollTargetId,
});

export const setScrolledToScrollTarget = (scrolledToScrollTarget: boolean): SetScrolledToScrollTarget => ({
  type: 'ChatList/SET_SCROLLED_TO_SCROLL_TARGET',
  scrolledToScrollTarget,
});

export const setScrolledToResult = (scrolledToSelectedResult: boolean): SetScrolledToResult => ({
  type: 'ChatList/SET_SCROLLED_TO_SEARCH_RESULT',
  scrolledToSelectedResult,
});

export const addedToChatroom = (
  roomId: number,
  isPushNotification: boolean,
  name: string,
  description: string,
  chatroomType: ChatroomType,
  managed: boolean,
  creatorId: number,
  memberCount: number,
  totalMessages: number | undefined,
  lastMessage: LastMessage | undefined,
  lastUpdateTime: string | undefined,
  lastActiveTime: string | undefined,
  donorId: number | undefined,
  organId: number | undefined,
  followerId: number | undefined,
  createDate: string,
  caseName?: string,
  followerName?: string,
): AddedToChatroom => ({
  type: 'ChatList/ADDED_TO_CHATROOM',
  roomId,
  isPushNotification,
  name,
  description,
  chatroomType,
  managed,
  creatorId,
  memberCount,
  totalMessages,
  lastMessage,
  lastUpdateTime,
  lastActiveTime,
  donorId,
  organId,
  followerId,
  createDate,
  caseName,
  followerName,
});

export const removedFromChatroom = (roomId: number): RemovedFromChatroom => ({
  type: 'ChatList/REMOVED_FROM_CHATROOM',
  roomId,
});

export const deletedChatroom = (roomId: number): DeletedChatroom => ({
  type: 'ChatList/DELETED_CHATROOM',
  roomId,
});

export const memberRemoved = (
  chatId: number,
  memberId: number,
  memberCount: number,
  leftChat: boolean,
): MemberRemoved => ({
  type: 'ChatList/MEMBER_REMOVED',
  chatId,
  memberId,
  memberCount,
  leftChat,
});

// This removes the member locally
export const removeMemberFromRoom = (chatId: number, memberId: number): RemoveMemberFromRoom => ({
  type: 'ChatList/REMOVE_MEMBER_FROM_ROOM',
  chatId,
  memberId,
});

export const clearChatroomNotifications = (chatId: number): ClearChatroomNotifications => ({
  type: 'ChatList/CLEAR_NOTIFICATIONS',
  chatId,
});

export const receiveChatroomInfo = (
  id: number,
  name: string,
  description: string,
  chatroomType: ChatroomType,
  managed: boolean,
  creatorId: number,
  memberCount: number,
  pinNumber: number | undefined,
  totalMessages: number,
  sentCount: number,
  readCount: number,
  clearedCount: number,
  lastMessage: LastMessage | undefined,
  lastUpdateTime: string | undefined,
  lastActiveTime: string | undefined,
  donorId: number | undefined,
  organId: number | undefined,
  targetOrgId: number | undefined,
  followerId: number | undefined,
  createDate: string,
  caseName?: string,
  followerName?: string,
): ReceiveChatroomInfo => ({
  type: 'ChatList/RECEIVE_ROOM_INFO',
  id,
  name,
  description,
  chatroomType,
  managed,
  creatorId,
  memberCount,
  pinNumber,
  totalMessages,
  sentCount,
  readCount,
  clearedCount,
  lastMessage,
  lastUpdateTime,
  lastActiveTime,
  donorId,
  organId,
  targetOrgId,
  followerId,
  createDate,
  caseName,
  followerName,
});

export const receiveBulkChatroomInfo = (
  chatrooms: Array<PartialChatroomInfo>,
  updateOrder: boolean,
): ReceiveBulkChatroomInfo => ({
  type: 'ChatList/BULK_RECEIVE_ROOMS',
  chatrooms,
  updateOrder,
});

export const updateChatroomInfo = (
  id: number,
  name: string | undefined,
  description: string,
  managed: boolean,
  lastUpdateTime?: string,
): UpdateChatroomInfo => ({
  type: 'ChatList/UPDATE_CHATROOM_INFO',
  id,
  name,
  description,
  managed,
  lastUpdateTime,
});

export const updateRoomIcon = (id: number, roomIcon: string | undefined): UpdateRoomIcon => ({
  type: 'ChatList/UPDATE_ROOM_ICON',
  id,
  roomIcon,
});

export const receiveRoomIconUpdate = (id: number, roomIcon: string | undefined): ReceiveRoomIconUpdate => ({
  type: 'ChatList/RECEIVE_ROOM_ICON_UPDATE',
  id,
  roomIcon,
});

export const openChatroom = (chatId: number): OpenChatroom => ({
  type: 'ChatList/OPEN_CHATROOM',
  chatId,
});

export const closeChatroom = (chatId: number): CloseChatroom => ({
  type: 'ChatList/CLOSE_CHATROOM',
  chatId,
});

export const removeChatroom = (chatId: number): RemoveChatroom => ({
  type: 'ChatList/REMOVE',
  chatId,
});

export const receiveChatroomMembers = (
  id: number,
  memberId: number,
  members: ChatroomMemberMap,
  memberOrder: Array<number>,
  hint: 'replace' | 'extend',
): ReceiveChatroomMembers => ({
  type: 'ChatList/RECEIVE_ROOM_MEMBERS',
  id,
  memberId,
  members,
  memberOrder,
  hint,
});

export const receiveBulkChatroomMembers = (
  memberId: number,
  rooms: Array<{
    id: number;
    members: ChatroomMemberMap;
    memberOrder: Array<number>;
  }>,
): ReceiveBulkChatroomMembers => ({
  type: 'ChatList/BULK_RECEIVE_MEMBERS',
  memberId,
  rooms,
});

export const loadChatMembers = (chatId: number): LoadChatMembers => ({
  type: 'ChatList/LOAD_MEMBERS',
  chatId,
});

export const loadPinnedMessages = (chatId: number): LoadPinnedMessages => ({
  type: 'ChatList/LOAD_PINNED_MESSAGES',
  chatId,
});

export const loadMessages = (
  chatId: number,
  memberId: number,
  resetMessages: boolean,
  numberMessages: number,
): LoadMessages => ({
  type: 'ChatList/LOAD_MESSAGES',
  chatId,
  memberId,
  resetMessages,
  numberMessages,
});

// See RefreshStaleMessages type above and loadChatroomMessagesManager in ChatSaga.ts for how
//  this differs from loadMessages: it always fetches from offset 0 rather than pulling in the
//  unread-backlog offset a true cold start uses, so it can't balloon into an unbounded fetch.
export const refreshStaleMessages = (
  chatId: number,
  memberId: number,
  numberMessages: number,
): RefreshStaleMessages => ({
  type: 'ChatList/REFRESH_STALE_MESSAGES',
  chatId,
  memberId,
  numberMessages,
});

export const receiveChatroomMessages = (
  id: number,
  memberId: number,
  messages: ChatroomMessageMap,
  messageOrder: Array<MessageId>,
  hint: 'end' | 'start' | 'replace',
  lastMessage: LastMessage | undefined,
  lastActiveTime: string | undefined,
  isOldPage: boolean,
  refreshedTime?: string,
): ReceiveChatroomMessages => ({
  type: 'ChatList/RECEIVE_ROOM_MESSAGES',
  id,
  memberId,
  messages,
  messageOrder,
  hint,
  lastMessage,
  lastActiveTime,
  isOldPage,
  refreshedTime,
});

export const receiveNewMessage = (
  id: number,
  memberId: number,
  message: ChatroomMessage,
  messageId: MessageId,
  hint: 'end' | 'start' | 'replace',
  lastMessage: LastMessage | undefined,
  lastActiveTime: string | undefined,
  sentCountModifier: number,
): ReceiveNewMessage => ({
  type: 'ChatList/RECEIVE_NEW_MESSAGE',
  id,
  memberId,
  message,
  messageId,
  hint,
  lastMessage,
  lastActiveTime,
  sentCountModifier,
});

export const attemptReceiveNewMessage = (
  id: number,
  memberId: number,
  message: ChatroomMessage,
  messageId: MessageId,
  hint: 'end' | 'start' | 'replace',
  lastMessage: LastMessage | undefined,
  lastActiveTime: string | undefined,
  sentCountModifier: number,
): AttemptReceiveNewMessage => ({
  type: 'ChatList/ATTEMPT_RECEIVE_NEW_MESSAGE',
  id,
  memberId,
  message,
  messageId,
  hint,
  lastMessage,
  lastActiveTime,
  sentCountModifier,
});

export const messageSendSuccess = (
  chatId: number,
  memberId: number,
  tmpId: string,
  newId: number,
  updateData: {
    status?: MessageSendStatus;
    sentTime: string;
    messageType: MessageType;
    textContent: string;
    fileName?: string;
    displayName?: string;
  },
): MessageSendSuccess => ({
  type: 'ChatList/MESSAGE_SUCCESS',
  chatId,
  memberId,
  tmpId,
  newId,
  updateData,
});

export const loadChatFiles = (chatId: number, memberId: number): LoadChatFiles => ({
  type: 'ChatList/LOAD_FILES',
  chatId,
  memberId,
});

export const receiveChatroomFiles = (
  id: number,
  fileMap: ChatroomFileMap,
  allFiles: Array<MessageId>,
  myFiles: Array<MessageId>,
): ReceiveChatroomFiles => ({
  type: 'ChatList/RECEIVE_ROOM_FILES',
  id,
  fileMap,
  allFiles,
  myFiles,
});

export const setSelectedFile = (id: number, selectedFile: ChatroomFile): SetSelectedFile => ({
  type: 'ChatList/SET_SELECTED_FILE',
  id,
  selectedFile,
});

export const setMessageStatus = (
  chatId: number,
  messageId: MessageId,
  status?: MessageSendStatus,
): SetMessageSendStatus => ({
  type: 'ChatList/MESSAGE_SEND_STATUS',
  chatId,
  messageId,
  status,
});

export const bulkUpdateMessageStatus = (
  chatId: number,
  memberId: number,
  statuses: {
    [key: string]: ReadStatusMap;
  },
  mode: 'extend' | 'replace',
  newStatusCount: number,
  updatedStatusCount: number,
): BulkUpdateMessageStatus => ({
  type: 'ChatList/BULK_UPDATE_MESSAGE_STATUS',
  chatId,
  memberId,
  statuses,
  mode,
  newStatusCount,
  updatedStatusCount,
});

export const setClearedNotificationCount = (chatId: number, newCount?: number): SetClearedNotificationCount => ({
  type: 'ChatList/SET_CLEARED_COUNT',
  chatId,
  newCount,
});

export const loadMessageStatuses = (
  chatId: number,
  memberId: number,
  messageIds: Array<number>,
): LoadMessageStatuses => ({
  type: 'ChatList/LOAD_MSG_STATUSES',
  chatId,
  memberId,
  messageIds,
});

export const markMessagesRead = (chatId: number, memberId: number, untilId: MessageId): MarkMessagesRead => ({
  type: 'ChatList/MARK_MESSAGES_READ',
  chatId,
  memberId,
  untilId,
});

export const triggerChatSocket = (): TriggerChatSocket => ({
  type: 'ChatList/SOCKET',
});

export const setSocketState = (
  status: ChatSocketStatus,
  error: ChatSocketError | undefined = undefined,
): SetChatSocketStatus => ({
  type: 'ChatList/SOCKET_STATUS',
  status,
  error,
});

export const setLastReconnectedTime = (time: string): SetLastReconnectedTime => ({
  type: 'ChatList/SET_LAST_RECONNECTED_TIME',
  time,
});

export const setOffset = (
  chatId: number,
  memberId: number,
  offset: number,
  numberMessages: number = CHAT_PAGE_SIZE,
): SetOffset => ({
  type: 'ChatList/SET_PAGE_OFFSET',
  chatId,
  memberId,
  offset,
  numberMessages,
});

export const updateDonorId = (chatId: number, donorId: number): UpdateDonorId => ({
  type: 'ChatList/UPDATE_DONOR_ID',
  chatId,
  donorId,
});

export const updateOrganId = (chatId: number, organId: number): UpdateOrganId => ({
  type: 'ChatList/UPDATE_ORGAN_ID',
  chatId,
  organId,
});

export const filterChatrooms = (filter: string): FilterChatrooms => ({
  type: 'ChatList/FILTER',
  filter,
});

export const filterOffers = (offerFilter: string): FilterOffers => ({
  type: 'ChatList/FILTER_OFFER',
  offerFilter,
});

export const filterOrganizations = (organizationFilter: string): FilterOrganizations => ({
  type: 'ChatList/FILTER_ORGANIZATION',
  organizationFilter,
});

export const filterPeople = (peopleFilter: string): FilterPeople => ({
  type: 'ChatList/FILTER_PEOPLE',
  peopleFilter,
});

export const resetFilters = (): ResetFilters => ({
  type: 'ChatList/RESET_FILTERS',
});

export const resetPeopleFilter = (): ResetPeopleFilter => ({
  type: 'ChatList/RESET_PEOPLE_FILTER',
});

export const acknowledgeMessage = (chatId: number, messageId: number, symbol: string): AcknowledgeMessage => ({
  type: 'ChatList/ACK_MESSAGE',
  chatId,
  messageId,
  symbol,
});

export const receiveAcknowledge = (
  chatId: number,
  messageId: number,
  memberId: number,
  symbol: string,
): ReceiveAcknowledge => ({
  type: 'ChatList/RECEIVE_ACK',
  chatId,
  messageId,
  memberId,
  symbol,
});

export const startTyping = (chatId: number): StartTyping => ({
  type: 'ChatList/START_TYPING',
  chatId,
});

export const stopTyping = (chatId: number): StopTyping => ({
  type: 'ChatList/STOP_TYPING',
  chatId,
});

export const memberStartedTyping = (chatId: number, memberId: number): MemberStartedTyping => ({
  type: 'ChatList/MEMBER_STARTED_TYPING',
  chatId,
  memberId,
});

export const memberStoppedTyping = (chatId: number, memberId: number): MemberStoppedTyping => ({
  type: 'ChatList/MEMBER_STOPPED_TYPING',
  chatId,
  memberId,
});

export const pinChatroom = (chatId: number): PinChatroom => ({
  type: 'ChatList/PIN_CHATROOM',
  chatId,
});

export const pinnedChatroom = (chatId: number, pinNumber: number): PinnedChatroom => ({
  type: 'ChatList/PINNED_CHATROOM',
  chatId,
  pinNumber,
});

export const unpinChatroom = (chatId: number): UnpinChatroom => ({
  type: 'ChatList/UNPIN_CHATROOM',
  chatId,
});

export const unpinnedChatroom = (chatId: number): UnpinnedChatroom => ({
  type: 'ChatList/UNPINNED_CHATROOM',
  chatId,
});

export const updatePinnedChatrooms = (pinnedChatrooms: { [key: string]: number }): UpdatePinnedChatrooms => ({
  type: 'ChatList/UPDATE_PINNED_CHATROOMS',
  pinnedChatrooms,
});

export const pinMessage = (chatId: number, messageId: number): PinMessage => ({
  type: 'ChatList/PIN_MESSAGE',
  chatId,
  messageId,
});

export const pinnedMessage = (chatId: number, messageId: number, pinOrder: number): PinnedMessage => ({
  type: 'ChatList/PINNED_MESSAGE',
  chatId,
  messageId,
  pinOrder,
});

export const unpinMessage = (chatId: number, messageId: number): UnpinMessage => ({
  type: 'ChatList/UNPIN_MESSAGE',
  chatId,
  messageId,
});

export const unpinnedMessage = (chatId: number, messageId: number): UnpinnedMessage => ({
  type: 'ChatList/UNPINNED_MESSAGE',
  chatId,
  messageId,
});

export const receivePinnedMessages = (
  chatId: number,
  pinnedMessagesMetadata: PinnedMessageMetadata[],
): ReceivePinnedMessages => ({
  type: 'ChatList/RECEIVE_PINNED_MESSAGES',
  chatId,
  pinnedMessagesMetadata,
});

export const redactMessage = (chatId: number, messageId: number): RedactMessage => ({
  type: 'ChatList/REDACT_MESSAGE',
  chatId,
  messageId,
});

export const redactMessageSuccess = (chatId: number, messageId: number, redactTime: string): RedactMessageSuccess => ({
  type: 'ChatList/REDACT_SUCCESS',
  chatId,
  messageId,
  redactTime,
});

export const unredactMessage = (chatId: number, messageId: number): UnredactMessage => ({
  type: 'ChatList/UNREDACT_MESSAGE',
  chatId,
  messageId,
});

export const unredactMessageSuccess = (chatId: number, messageId: number): UnredactMessageSuccess => ({
  type: 'ChatList/UNREDACT_SUCCESS',
  chatId,
  messageId,
});

export const editMessage = (chatId: number, messageId: number, textContent: string): EditMessage => ({
  type: 'ChatList/EDIT_MESSAGE',
  chatId,
  messageId,
  textContent,
});

export const editMessageSuccess = (
  chatId: number,
  messageId: number,
  textContent: string,
  editTime: string,
  editedBy: number,
): EditMessageSuccess => ({
  type: 'ChatList/EDIT_SUCCESS',
  chatId,
  messageId,
  textContent,
  editTime,
  editedBy,
});

export const deleteFileMessage = (chatId: number, messageId: number): DeleteFileMessage => ({
  type: 'ChatList/DELETE_FILE_MESSAGE',
  chatId,
  messageId,
});

export const deleteFileMessageSuccess = (chatId: number, messageId: number): DeleteFileMessageSuccess => ({
  type: 'ChatList/DELETE_FILE_MESSAGE_SUCCESS',
  chatId,
  messageId,
});

export const receiveRoomDoNotDisturb = (chatId: number, doNotDisturb: boolean): ReceiveRoomDoNotDisturb => ({
  type: 'ChatList/RECEIVE_ROOM_DO_NOT_DISTURB',
  chatId,
  doNotDisturb,
});

export const receiveAddChatroomManager = (chatId: number, userId: number): ReceiveAddChatroomManager => ({
  type: 'ChatList/RECEIVE_ADD_CHATROOM_MANAGER',
  chatId,
  userId,
});

export const receiveRemoveChatroomManager = (chatId: number, userId: number): ReceiveRemoveChatroomManager => ({
  type: 'ChatList/RECEIVE_REMOVE_CHATROOM_MANAGER',
  chatId,
  userId,
});

export const receiveChatroomManagers = (chatId: number, managerIds: number[]): ReceiveChatroomManagers => ({
  type: 'ChatList/RECEIVE_CHATROOM_MANAGERS',
  chatId,
  managerIds,
});

type RequiredChatroomKeys =
  | 'id'
  | 'name'
  | 'donorId'
  | 'organId'
  | 'chatroomType'
  | 'description'
  | 'managed'
  | 'creatorId'
  | 'memberCount'
  | 'createDate'
  | 'donorClosed'
  | 'selectedFile'
  | 'doNotDisturb';

const defaultChatroom: Omit<ChatroomInfo, RequiredChatroomKeys> = {
  memberOrder: [],
  managerIds: [],
  members: {},
  memberNames: [],
  messageOrder: [],
  messages: {},
  pinnedMessages: {},
  pinnedOrder: [],
  readStatus: {},
  lastFailedId: undefined,
  notificationSound: NotificationSoundMap.default,
  sentCount: 0,
  readCount: 0,
  clearedCount: 0,
  lastMessage: undefined,
  priorityNotificationSound: NotificationSoundMap.default,

  membersTyping: [],
  files: {},
  allFiles: [],
  myFiles: [],

  offset: 0,
  totalMessages: 0,
};

const mergeChatroomInfoWithDefaults = (chatroom: ChatroomInfo): ChatroomInfo => ({
  ...defaultChatroom,
  ...chatroom,
});

const mergeMessages = (chatInfo: ChatroomInfo, action: ReceiveChatroomMessages) => {
  const { memberId, messages, messageOrder, hint, lastMessage, lastActiveTime, isOldPage, refreshedTime } = action;

  // In some cases the message in the room might come before the room information is available
  //  using defaults for that case ensures we do not crash in this method
  const fullChatInfo = mergeChatroomInfoWithDefaults(chatInfo);
  let newMessageOrder: MessageId[];

  const originalCount = fullChatInfo.readCount;
  let newCount = originalCount || 0;

  switch (hint) {
    //  Note: This clears the messages that have been `deleted` on the server. However we do not have the same logic
    //  for readStatus so statuses will stay in the store until the user logs out or is removed
    //  from the chatroom.
    case 'replace':
      newMessageOrder = messageOrder.slice();
      newCount = 0;

      newMessageOrder.forEach((msgId) => {
        if (!messages[`${msgId}`].seenByCurrentUser && messages[`${msgId}`].senderId !== memberId) {
          newCount += 1;
        }
      });

      break;

    case 'start': {
      const addToStart: MessageId[] = [];
      newMessageOrder = fullChatInfo.messageOrder.slice();

      messageOrder.forEach((msgId) => {
        if (newMessageOrder.indexOf(msgId) === -1) {
          addToStart.push(msgId);
        }

        // We do not care about older pages...
        if (!isOldPage) {
          // Already seen messages and messages from myself should not matter in unread count
          if (!messages[`${msgId}`].seenByCurrentUser && messages[`${msgId}`].senderId !== memberId) {
            newCount += 1;
          }
        }
      });

      newMessageOrder = addToStart.concat(newMessageOrder);

      break;
    }

    case 'end':
    default:
      newMessageOrder = fullChatInfo.messageOrder.slice();

      messageOrder.forEach((msgId) => {
        if (newMessageOrder.indexOf(msgId) === -1) {
          newMessageOrder.push(msgId);
        }

        // We do not care about older pages...
        if (!isOldPage) {
          // Already seen messages and messages from myself should not matter in unread count
          if (!messages[`${msgId}`].seenByCurrentUser && messages[`${msgId}`].senderId !== memberId) {
            newCount += 1;
          }
        }
      });
  }

  // Update lastMessage if provided
  const newLastMessage = lastMessage || fullChatInfo.lastMessage;

  // Determine least recent unread message by the current user
  const updatedMessages = {
    ...(hint !== 'replace' ? fullChatInfo.messages : {}),
    ...messages,
  };

  let offset = 0;
  if (isOldPage) {
    offset = fullChatInfo.offset;
  } else {
    // Start where the user left off
    offset = newCount > CHAT_PAGE_SIZE ? Math.floor(newCount / CHAT_PAGE_SIZE) * CHAT_PAGE_SIZE : fullChatInfo.offset;
  }
  // Make sure we clamp offset if messages were replaced and offset is too big now
  if (hint === 'replace' && offset >= newMessageOrder.length) {
    offset = Math.floor(newMessageOrder.length / CHAT_PAGE_SIZE) * CHAT_PAGE_SIZE;
  }

  // Only a full 'replace' reload actually re-fetches every currently-cached message with fresh
  //  presigned URLs, so only 'replace' is allowed to move this timestamp forward. Incremental
  //  'start'/'end' merges (pagination, real-time messages) leave it untouched - they don't
  //  refresh anything already in the store, so they can't vouch for its freshness.
  const messagesRefreshedTime = hint === 'replace' ? refreshedTime : fullChatInfo.messagesRefreshedTime;

  return {
    messages: updatedMessages,
    messageOrder: newMessageOrder,
    lastMessage: newLastMessage,
    lastActiveTime,
    messagesRefreshedTime,
    offset,
  };
};

const receivedNewMessageReducer = (state: ChatListState, action: ReceiveNewMessage): ChatListState => {
  const chatInfo = state.chats[`${action.id}`];

  if (chatInfo === undefined) {
    return {
      ...state,
    };
  }

  const newLastActiveTime = action.lastActiveTime ? action.lastActiveTime : chatInfo.lastActiveTime;

  let fileUpdates: Partial<ChatroomInfo> = {};
  if (FileMessageTypes.includes(action.message.messageType)) {
    const newFile: ChatroomFile = {
      id: action.messageId,
      fileName: action.message.fileName || action.message.displayName || '',
      displayName: action.message.displayName,
      localPath: action.message.localPath,
      messageType: action.message.messageType,
      size: 0,
      sentTime: action.message.sentTime,
      memberId: action.message.senderId,
    };

    const newAllFiles = chatInfo.allFiles.includes(action.messageId)
      ? chatInfo.allFiles
      : [...chatInfo.allFiles, action.messageId];

    const newMyFiles =
      action.message.senderId === action.memberId && !chatInfo.myFiles.includes(action.messageId)
        ? [...chatInfo.myFiles, action.messageId]
        : chatInfo.myFiles;

    fileUpdates = {
      files: {
        ...chatInfo.files,
        [`${action.messageId}`]: newFile,
      },
      allFiles: newAllFiles,
      myFiles: newMyFiles,
    };
  }

  return {
    ...state,

    chats: {
      ...state.chats,
      [action.id]: {
        ...chatInfo,
        ...mergeMessages(
          chatInfo,
          receiveChatroomMessages(
            action.id,
            action.memberId,
            {
              // $FlowFixMe
              [action.messageId]: action.message,
            },
            [action.messageId],
            action.hint,
            action.lastMessage,
            newLastActiveTime,
            false,
          ),
        ),

        ...fileUpdates,

        // Increment sent count if needed
        sentCount: ((chatInfo ? chatInfo.sentCount : 0) || 0) + (action.sentCountModifier || 0),

        // Increment total message count of this room
        totalMessages: ((chatInfo ? chatInfo.totalMessages : 0) || 0) + 1,
      },
    },
  };
};

const unreadMessageCountReducer = (
  chatInfo: ChatroomInfo | undefined,
  action: ReceiveChatroomInfo | PartialChatroomInfo,
) => {
  const unreadCount = getBadgeCount(action);

  if (unreadCount !== null) {
    if (unreadCount >= 0) {
      return unreadCount || 0;
    }

    devLog('lifecycle', [`Got unexpected unread message count for room id ${action.id}: ${unreadCount}`]);
    Sentry.captureException(new Error('Invalid unread message count'), { extra: { chatInfo, action, unreadCount } });
  }

  if (chatInfo) {
    return getBadgeCount(chatInfo) || 0;
  }

  return 0;
};

/**
 * Returns seenByCount for the message and an updated read status map
 */
const messageSuccessReadReducer = (state: ReadStatusMap, action: MessageSendSuccess): [number, ReadStatusMap] => {
  const { newId, tmpId } = action;

  if (!state[tmpId]) {
    return [0, state];
  }

  // Also make sure we do not lose read statuses attached to temporary id
  const oldData = state[tmpId];

  // This ensures we do not lose any statuses if we receive the status before the message info
  const possibleIdData = state[`${newId}`] || {};

  const newState = { ...state };
  delete newState[tmpId];
  newState[newId.toString()] = {
    ...oldData,
    ...possibleIdData,
  };

  const seenByCount = values(newState[newId.toString()]).filter((readTime?: string) => !!readTime).length;
  return [seenByCount, newState];
};

const messageSuccessReducer = (chatInfo: ChatroomInfo, action: MessageSendSuccess): ChatroomInfo => {
  const { newId, tmpId, updateData } = action;

  const [seenByCount, readStatus] = messageSuccessReadReducer(chatInfo.readStatus, action);

  // Create an updated version of the message data
  const msgInfo: ChatroomMessage = {
    ...chatInfo.messages[tmpId],

    id: newId,

    ...updateData,
    seenByCount,
    status: 'delivered',
  };

  // Delete the original from map
  const newMessages = { ...chatInfo.messages };
  delete newMessages[tmpId];
  newMessages[newId.toString()] = msgInfo;

  // Update ordering map
  const newOrder = chatInfo.messageOrder.slice();
  newOrder.splice(newOrder.indexOf(tmpId), 1, newId);

  return {
    ...chatInfo,
    readStatus,

    messages: newMessages,
    messageOrder: newOrder,

    // Increment total message count of this room
    totalMessages: ((chatInfo ? chatInfo.totalMessages : 0) || 0) + 1,
  };
};

const setSingleMessageStatusReducer = (
  state: ChatroomInfo,
  memberId: number,
  messageId: number,
  statuses: MessageReadStatus,
  mode: 'replace' | 'extend',
): ChatroomInfo => {
  if (!state.messages[`${messageId}`]) {
    return {
      ...state,
    };
  }

  let { seenByCount } = state.messages[`${messageId}`];
  let newStatuses: MessageReadStatus;
  let lastMessageStatusReceived = state.lastMessageStatusReceived || undefined;

  if (mode === 'replace') {
    newStatuses = { ...statuses };
    const statusKeys = keys(newStatuses);
    const statusCount = statusKeys.length;
    seenByCount = statusCount;

    if (statusCount > 0) {
      lastMessageStatusReceived = newStatuses[statusKeys[statusCount - 1]];
    }
  } else {
    newStatuses = { ...(state.readStatus[`${messageId}`] || {}) };

    const chatMemberIds = keys(statuses);

    chatMemberIds.forEach((member: string) => {
      // If message status does not exist locally, increment local seenByCount
      if (!newStatuses[member]) {
        seenByCount += 1;
      }

      newStatuses[member] = statuses[member];
    });

    if (chatMemberIds.length > 0) {
      lastMessageStatusReceived = newStatuses[chatMemberIds[chatMemberIds.length - 1]];
    }
  }

  // TXP-1999 - seenByCurrentUser was only being set if the new status was for the current user
  //            but own messages should also be set as seenByCurrentUser
  const { senderId } = state.messages[messageId.toString()];
  const seenByCurrent = memberId === senderId || !!newStatuses[`${memberId}`];

  return {
    ...state,

    lastMessageStatusReceived,
    messages: {
      ...state.messages,

      [`${messageId}`]: {
        ...(state.messages[`${messageId}`] || {}),

        seenByCount,
        seenByCurrentUser: seenByCurrent,
      },
    },

    readStatus: {
      ...state.readStatus,

      [`${messageId}`]: newStatuses,
    },
  };
};

const setMessageStatusesReducer = (state: ChatListState, action: BulkUpdateMessageStatus): ChatListState => {
  const { memberId, statuses } = action;

  const resState: ChatListState = {
    ...state,

    chats: {
      ...state.chats,

      [action.chatId]: {
        ...state.chats[`${action.chatId}`],

        readCount: state.chats[`${action.chatId}`].readCount + action.newStatusCount + action.updatedStatusCount,
        clearedCount: state.chats[`${action.chatId}`].clearedCount - action.updatedStatusCount,
      },
    },
  };

  keys(statuses).forEach((chatId: string) => {
    keys(statuses[chatId]).forEach((msgId: string) => {
      const messageIdInt = parseInt(msgId, 10);
      const messageStatuses = statuses[chatId][msgId];
      resState.chats[chatId] = setSingleMessageStatusReducer(
        resState.chats[chatId],
        memberId,
        messageIdInt,
        messageStatuses,
        action.mode,
      );
    });
  });

  return resState;
};

const updatePinnedChatroomsReducer = (state: ChatListState, action: UpdatePinnedChatrooms): ChatListState => {
  const { pinnedChatrooms } = action;

  const resState = {
    ...state,

    chats: {
      ...state.chats,
    },
  };

  if (pinnedChatrooms) {
    keys(pinnedChatrooms).forEach((chatId: string) => {
      resState.chats[chatId] = {
        ...resState.chats[chatId],
        pinNumber: pinnedChatrooms[chatId],
      };
    });
  }

  return resState;
};

const removeChatroomReducer = (state: ChatListState, action: RemoveChatroom): ChatListState => {
  const chats = { ...state.chats };
  delete chats[`${action.chatId}`];
  const activeChatId = action.chatId === state.activeChatId ? undefined : state.activeChatId;
  const derivedOrder = Object.keys(chats).map(Number);

  return {
    ...state,
    order: derivedOrder,
    chats,
    activeChatId,
  };
};

const isRoomLoaded = (state: ChatListState, chatId: number): boolean => {
  const chat = state.chats[`${chatId}`];
  return Boolean(chat);
};

const reducer = (state: ChatListState = initialState, action: ChatListAction): ChatListState => {
  // wrap the whole reducer to catch any unexpected exceptions and record them
  // Another component will need to act upon these and potentially send them to Sentry
  try {
    return innerReducer(state, action);
  } catch (err) {
    const reducerErrors = state.reducerErrors.slice();
    const uuid = createUuid(0);
    if (err instanceof Error) {
      reducerErrors.push(getReducerError(uuid, 'chatList', action, err));
    } else {
      const unknownError = new Error('An unknown error occurred in ChatListActions');
      reducerErrors.push(getReducerError(uuid, 'chatList', action, unknownError));
    }
    return {
      ...state,

      reducerErrors,
    };
  }
};

const innerReducer = (state: ChatListState = initialState, action: ChatListAction): ChatListState => {
  switch (action.type) {
    case 'Application/RESET_ERROR': {
      const reducerErrors = resetReducerErrors(state.reducerErrors, action);
      return {
        ...state,
        reducerErrors,
      };
    }

    // On refresh/reconnect reset most things
    case 'ChatList/RESET_DATA':
      return {
        ...state,

        order: [],
        chats: {} as ChatroomInfoMap,
        isTyping: false,
        filter: '',
        offerFilter: '',
        organizationFilter: '',
        peopleFilter: '',
        searchResults: {},
        selectedResult: {} as SearchResult,
        scrolledToSelectedResult: false,
        selectedScrollTargetId: undefined,
        scrolledToScrollTarget: false,
        deepSearchFilter: '',
      };

    case 'Application/RESET_DATA':
    case 'Auth/LOGOUT':
      // Reset the store when logging out
      return {
        ...initialState,
      };

    case 'ChatList/RECEIVE_ROOM_INFO': {
      const unreadCount = unreadMessageCountReducer(state.chats[`${action.id}`], action);
      const currentCreateDate = state.chats[`${action.id}`] ? state.chats[`${action.id}`].createDate : null;

      // Start where the user left off
      const offset = unreadCount > CHAT_PAGE_SIZE ? Math.floor(unreadCount / CHAT_PAGE_SIZE) * CHAT_PAGE_SIZE : 0;

      const newOrder = state.order.slice(0);
      // add to the order if not already there
      if (!newOrder.includes(action.id)) newOrder.push(action.id);

      const chat = state.chats[`${action.id}`];
      const mergedRoomInfo = mergeChatroomInfoWithDefaults(chat);

      return {
        ...state,
        order: newOrder,
        chats: {
          ...state.chats,

          [action.id]: {
            status: 'unknown',

            lastFailedId: undefined,

            pinNumber: action.pinNumber || null,

            ...mergedRoomInfo,

            id: action.id,
            name: action.name,
            description: action.description,
            chatroomType: action.chatroomType,
            managed: action.managed,
            creatorId: action.creatorId,
            lastMessage: action.lastMessage,
            lastUpdateTime: action.lastUpdateTime || null,
            lastActiveTime: action.lastActiveTime || null,
            memberCount: action.memberCount || 0,

            totalMessages: action.totalMessages || 0,

            offset,
            sentCount: hasValue(action.sentCount)
              ? action.sentCount
              : (state.chats[`${action.id}`] || {}).sentCount || 0,
            readCount: hasValue(action.readCount)
              ? action.readCount
              : (state.chats[`${action.id}`] || {}).readCount || 0,
            clearedCount: hasValue(action.clearedCount)
              ? action.clearedCount
              : (state.chats[`${action.id}`] || {}).clearedCount || 0,
            donorId: action.donorId,
            organId: action.organId,
            targetOrgId: action.targetOrgId,
            followerId: action.followerId || null,
            // Sometimes we're only updating some info, so default to current create date if one isn't provided
            createDate: action.createDate || currentCreateDate,
            caseName: action.caseName,
            followerName: action.followerName,
            roomIcon: action.roomIcon !== undefined ? action.roomIcon : (state.chats[`${action.id}`] || {}).roomIcon,
          },
        },
      };
    }

    // NOTE: We are currently keeping all chats that have been loaded on state. This helps to reduce
    // the risk of trying to access content on a room that hasn't been loaded. Because we utilize these rooms
    // in different contexts where we might want to display different rooms, we want to move away from storing these
    // on state altogether, and instead just fetching rooms with hooks as we need them.
    case 'ChatList/BULK_RECEIVE_ROOMS': {
      const newRooms: ChatroomInfoMap = {};
      const activeRoom: ChatroomInfoMap = {};
      const { activeChatId, chats } = state;
      if (activeChatId) {
        const activeRoomInReceived = action.chatrooms.find((roomInfo) => roomInfo.id === activeChatId);
        if (!activeRoomInReceived) {
          activeRoom[`${activeChatId}`] = chats[`${activeChatId}`];
        }
      }

      action.chatrooms.forEach((roomInfo) => {
        const unreadCount = unreadMessageCountReducer(state.chats[`${roomInfo.id}`], roomInfo);

        // Start where the user left off
        const offset = unreadCount > CHAT_PAGE_SIZE ? Math.floor(unreadCount / CHAT_PAGE_SIZE) * CHAT_PAGE_SIZE : 0;

        const oldRoomInfo = state.chats[`${roomInfo.id}`];
        const mergedRoomInfo = mergeChatroomInfoWithDefaults(oldRoomInfo);

        newRooms[`${roomInfo.id}`] = {
          ...mergedRoomInfo,

          id: roomInfo.id,
          name: roomInfo.name,
          description: roomInfo.description,
          chatroomType: roomInfo.chatroomType,
          managed: roomInfo.managed,
          managerIds: roomInfo.managerIds || [],
          creatorId: roomInfo.creatorId ? roomInfo.creatorId : (state.chats[`${roomInfo.id}`] || {}).creatorId,
          lastMessage: roomInfo.lastMessage,
          lastUpdateTime: roomInfo.lastUpdateTime || undefined,
          lastActiveTime: roomInfo.lastActiveTime || undefined,
          doNotDisturb: roomInfo.doNotDisturb,
          pinNumber: roomInfo.pinNumber,
          membersTyping: [],
          memberCount: roomInfo.memberCount || 0,
          totalMessages: roomInfo.totalMessages || 0,
          sentCount: hasValue(roomInfo.sentCount)
            ? roomInfo.sentCount
            : (state.chats[`${roomInfo.id}`] || {}).sentCount || 0,
          clearedCount: hasValue(roomInfo.clearedCount)
            ? roomInfo.clearedCount
            : (state.chats[`${roomInfo.id}`] || {}).clearedCount || 0,
          readCount: hasValue(roomInfo.readCount)
            ? roomInfo.readCount
            : (state.chats[`${roomInfo.id}`] || {}).readCount || 0,

          offset,
          donorId: roomInfo.donorId,
          organId: roomInfo.organId,
          organType: roomInfo.organType,
          targetOrgId: roomInfo.targetOrgId,
          createDate: roomInfo.createDate,
          donorClosed: roomInfo.donorClosed,
          followerId: roomInfo.followerId,
          caseName: roomInfo.caseName,
          followerName: roomInfo.followerName,
          roomIcon:
            roomInfo.roomIcon !== undefined ? roomInfo.roomIcon : (state.chats[`${roomInfo.id}`] || {}).roomIcon,
        };
      });

      // NOTE: see summary at top of case for this action about keeping all chats
      const fullChatList: ChatroomInfoMap = {
        ...chats,
        ...activeRoom,
        ...newRooms,
      };
      const newOrder = action.updateOrder ? action.chatrooms.map((room) => room.id) : [...state.order];

      return {
        ...state,

        chats: fullChatList,
        order: newOrder,
      };
    }

    case 'ChatList/UPDATE_CHATROOM_INFO': {
      if (!isRoomLoaded(state, action.id)) {
        return state;
      }
      return {
        ...state,

        chats: {
          ...state.chats,

          [action.id]: {
            ...state.chats[`${action.id}`],

            name: action.name || (state.chats[`${action.id}`] || {}).name || '$no-name$',
            description:
              action.description || (state.chats[`${action.id}`] || {}).description || 'No description for room.',
            managed:
              (action.managed === undefined ? (state.chats[`${action.id}`] || {}).managed : action.managed) || false,
            lastUpdateTime: action.lastUpdateTime || (state.chats[`${action.id}`] || {}).lastUpdateTime || null,
          },
        },
      };
    }

    case 'ChatList/UPDATE_ROOM_ICON':
    case 'ChatList/RECEIVE_ROOM_ICON_UPDATE': {
      if (!isRoomLoaded(state, action.id)) {
        return state;
      }
      return {
        ...state,

        chats: {
          ...state.chats,

          [action.id]: {
            ...state.chats[`${action.id}`],

            roomIcon: action.roomIcon,
          },
        },
      };
    }

    case 'ChatList/REMOVE': {
      return removeChatroomReducer(state, action);
    }

    case 'ChatList/REMOVE_MEMBER_FROM_ROOM':
    case 'ChatList/MEMBER_REMOVED': {
      if (!isRoomLoaded(state, action.chatId)) {
        return state;
      }
      const newMembers: ChatroomMemberMap = {
        ...state.chats[`${action.chatId}`].members,

        [`${action.memberId}`]: {
          ...state.chats[`${action.chatId}`].members[`${action.memberId}`],

          membershipStatus: action.type === 'ChatList/MEMBER_REMOVED' && action.leftChat ? 'Left' : 'Removed',
        },
      };

      const newMemberCount =
        (action.type === 'ChatList/MEMBER_REMOVED' && action.memberCount) ||
        keys(newMembers).filter((memberId) => newMembers[memberId].membershipStatus === 'Present').length;

      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            members: newMembers,
            memberCount: newMemberCount,
          },
        },
      };
    }

    case 'ChatList/RECEIVE_ROOM_MEMBERS': {
      if (!isRoomLoaded(state, action.id)) {
        return state;
      }
      const memberOrder =
        action.hint === 'replace' ? action.memberOrder : state.chats[`${action.id}`].memberOrder.slice();

      if (action.hint === 'extend') {
        action.memberOrder.forEach((memberId) => {
          if (memberOrder.indexOf(memberId) === -1) {
            memberOrder.push(memberId);
          }
        });
      }

      const actionMembers = action.members;
      const myId = action.memberId;
      const members = action.hint === 'replace' ? actionMembers : { ...state.chats[`${action.id}`].members };
      const memberNames: string[] = [];

      let { notificationSound, priorityNotificationSound } = state.chats[`${action.id}`];

      keys(actionMembers).forEach((memberId) => {
        if (`${memberId}` === `${myId}`) {
          notificationSound =
            (hasValue(actionMembers[`${memberId}`].notificationSound)
              ? actionMembers[`${memberId}`].notificationSound
              : (members[`${memberId}`] || {}).notificationSound) || NotificationSoundMap.default;

          priorityNotificationSound =
            (hasValue(actionMembers[`${memberId}`].priorityNotificationSound)
              ? actionMembers[`${memberId}`].priorityNotificationSound
              : (members[`${memberId}`] || {}).priorityNotificationSound) || NotificationSoundMap.default;
        }

        members[`${memberId}`] = {
          ...members[`${memberId}`],
          ...actionMembers[`${memberId}`],

          notificationSound,
        };
        if (members[`${memberId}`].membershipStatus === 'Present') {
          memberNames.push(selectProfileName(members[`${memberId}`].profile as UserProfile));
        }
      });

      const newMemberCount = keys(members).filter(
        (memberId) => members[memberId] && members[memberId].membershipStatus === 'Present',
      ).length;

      return {
        ...state,
        chats: {
          ...state.chats,
          [action.id]: {
            ...state.chats[`${action.id}`],

            notificationSound,
            priorityNotificationSound,

            memberOrder,
            members,
            memberCount: newMemberCount,
            memberNames,
          },
        },
      };
    }

    case 'ChatList/BULK_RECEIVE_MEMBERS': {
      const updatedRooms: ChatroomInfoMap = {};

      const memberNames: string[] = [];

      action.rooms.forEach((roomMembers) => {
        const theMembers = roomMembers.members;

        for (let i = 0; i < keys(theMembers).length; i += 1) {
          const memberId = keys(theMembers)[i];

          if (theMembers[`${memberId}`].membershipStatus === 'Present') {
            memberNames.push(selectProfileName(theMembers[`${memberId}`].profile as UserProfile));
          }
        }

        updatedRooms[`${roomMembers.id}`] = {
          ...state.chats[`${roomMembers.id}`],

          memberOrder: roomMembers.memberOrder,
          members: theMembers,
          memberNames,
        };
      });

      return {
        ...state,

        chats: {
          ...state.chats,

          ...updatedRooms,
        },
      };
    }

    case 'ChatList/RECEIVE_ROOM_MESSAGES':
      return {
        ...state,

        chats: {
          ...state.chats,
          [action.id]: {
            ...state.chats[`${action.id}`],
            ...mergeMessages(state.chats[`${action.id}`], action),
          },
        },
      };

    case 'ChatList/RECEIVE_NEW_MESSAGE':
      return receivedNewMessageReducer(state, action);

    case 'ChatList/MESSAGE_SUCCESS':
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: messageSuccessReducer(state.chats[`${action.chatId}`], action),
        },
      };

    case 'ChatList/MESSAGE_SEND_STATUS':
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            lastFailedId:
              (action.status === 'failed' ? `${action.messageId}` : undefined) ||
              (state.chats[`${action.chatId}`] || {}).lastFailedId,

            messages: {
              ...state.chats[`${action.chatId}`].messages,

              [`${action.messageId}`]: {
                ...state.chats[`${action.chatId}`].messages[`${action.messageId}`],

                status: action.status,
              },
            },
          },
        },
      };

    // Note: Reducer always replaces local files
    case 'ChatList/RECEIVE_ROOM_FILES':
      return {
        ...state,

        chats: {
          ...state.chats,
          [action.id]: {
            ...state.chats[`${action.id}`],

            files: action.fileMap,
            allFiles: action.allFiles,
            myFiles: action.myFiles,
          },
        },
      };

    case 'ChatList/SET_SELECTED_FILE':
      return {
        ...state,

        chats: {
          ...state.chats,
          [action.id]: {
            ...state.chats[`${action.id}`],

            selectedFile: action.selectedFile,
          },
        },
      };

    case 'ChatList/BULK_UPDATE_MESSAGE_STATUS':
      if (!isRoomLoaded(state, action.chatId)) {
        return state;
      }
      return setMessageStatusesReducer(state, action);

    case 'ChatList/SOCKET_STATUS': {
      return {
        ...state,

        socketStatus: action.status,
        socketError: action.error,
      };
    }

    case 'ChatList/SET_LAST_RECONNECTED_TIME': {
      return {
        ...state,

        lastReconnectedTime: action.time,
      };
    }

    case 'ChatEdit/SUCCESS':
    case 'ChatList/OPEN_CHATROOM': {
      return {
        ...state,

        activeChatId: action.chatId,
      };
    }

    case 'ChatList/CLOSE_CHATROOM': {
      const activeChat = state.activeChatId;

      return {
        ...state,

        activeChatId: activeChat === action.chatId ? undefined : activeChat,
      };
    }

    case 'ChatList/SET_PAGE_OFFSET': {
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            offset: action.offset,
          },
        },
      };
    }

    case 'ChatList/UPDATE_DONOR_ID': {
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            donorId: action.donorId,
          },
        },
      };
    }

    case 'ChatList/UPDATE_ORGAN_ID': {
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            organId: action.organId,
          },
        },
      };
    }

    case 'ChatList/FILTER': {
      const currentSearchResults = state.searchResults;
      const currentSelectedResult = state.selectedResult;
      const currentDeepSearchFilter = state.deepSearchFilter;
      return {
        ...state,

        filter: action.filter,
        searchResults: action.filter === '' ? {} : currentSearchResults,
        selectedResult: action.filter === '' ? ({} as SearchResult) : currentSelectedResult,
        deepSearchFilter: action.filter === '' ? '' : currentDeepSearchFilter,
      };
    }

    case 'ChatList/FILTER_OFFER': {
      return {
        ...state,

        offerFilter: action.offerFilter,
      };
    }

    case 'ChatList/FILTER_ORGANIZATION': {
      return {
        ...state,

        organizationFilter: action.organizationFilter,
      };
    }

    case 'ChatList/FILTER_PEOPLE': {
      return {
        ...state,

        peopleFilter: action.peopleFilter,
      };
    }

    case 'ChatList/RESET_FILTERS': {
      return {
        ...state,

        offerFilter: '',
        organizationFilter: '',
        peopleFilter: '',
      };
    }

    case 'ChatList/RESET_PEOPLE_FILTER': {
      return {
        ...state,
        peopleFilter: '',
      };
    }

    case 'ChatList/RECEIVE_ACK': {
      // Defensively update the ack if it is in state (meaning the room has been opened or received messages)
      if (
        !state.chats ||
        !state.chats[`${action.chatId}`] ||
        !state.chats[`${action.chatId}`].messages ||
        !state.chats[`${action.chatId}`].messages[`${action.messageId}`]
      ) {
        return state;
      }

      const newAck = {
        chatId: action.chatId,
        messageId: action.messageId,
        memberId: action.memberId,
        symbol: action.symbol,
      };
      return {
        ...state,
        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            messages: {
              ...state.chats[`${action.chatId}`].messages,

              [`${action.messageId}`]: {
                ...state.chats[`${action.chatId}`].messages[`${action.messageId}`],

                ack: {
                  ...state.chats[`${action.chatId}`].messages[`${action.messageId}`].ack,

                  [`${action.memberId}`]: newAck,
                },
              },
            },
          },
        },
      };
    }

    case 'ChatList/START_TYPING': {
      return {
        ...state,

        isTyping: true,
      };
    }

    case 'ChatList/STOP_TYPING': {
      return {
        ...state,

        isTyping: false,
      };
    }

    case 'ChatList/MEMBER_STARTED_TYPING': {
      if (!isRoomLoaded(state, action.chatId)) {
        return state;
      }
      // Use slice to create a new array - or else React may not 'react' to the change in the array content
      const currentMembersTyping = state.chats[`${action.chatId}`].membersTyping.slice() || [];

      // if action.memberId is not already in membersTyping array
      if (!currentMembersTyping.includes(action.memberId)) {
        currentMembersTyping.push(action.memberId);
        return {
          ...state,
          chats: {
            ...state.chats,

            [`${action.chatId}`]: {
              ...state.chats[`${action.chatId}`],

              membersTyping: currentMembersTyping,
            },
          },
        };
      }
      return {
        ...state,
      };
    }

    case 'ChatList/MEMBER_STOPPED_TYPING': {
      if (!isRoomLoaded(state, action.chatId)) {
        return state;
      }
      // Use slice to create a new array - or else React may not 'react' to the change in the array content
      const currentMembersTyping = state.chats[`${action.chatId}`].membersTyping.slice() || [];
      const spliceIndex = currentMembersTyping.indexOf(action.memberId);

      if (spliceIndex !== -1) {
        currentMembersTyping.splice(spliceIndex, 1);
        return {
          ...state,
          chats: {
            ...state.chats,

            [`${action.chatId}`]: {
              ...state.chats[`${action.chatId}`],

              membersTyping: currentMembersTyping,
            },
          },
        };
      }
      return {
        ...state,
      };
    }

    case 'ChatList/PINNED_CHATROOM': {
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            pinNumber: action.pinNumber,
          },
        },
      };
    }

    case 'ChatList/UNPINNED_CHATROOM': {
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            pinNumber: undefined,
          },
        },
      };
    }

    case 'ChatList/UPDATE_PINNED_CHATROOMS':
      return updatePinnedChatroomsReducer(state, action);

    case 'ChatList/PINNED_MESSAGE': {
      if (!state.chats[`${action.chatId}`]) {
        return state;
      }

      const chatroom = state.chats[`${action.chatId}`];
      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...chatroom,
            pinnedMessages: {
              ...chatroom.pinnedMessages,
              [`${action.messageId}`]: {
                id: action.messageId,
                pinOrder: action.pinOrder,
                pinnedBy: 0, // populated from API
                pinnedTime: new Date().toISOString(),
                message: chatroom.messages[`${action.messageId}`] || null,
              },
            },
            pinnedOrder: [...(chatroom.pinnedOrder || []), action.messageId],
          },
        },
      };
    }

    case 'ChatList/UNPINNED_MESSAGE': {
      if (!state.chats[`${action.chatId}`]) {
        return state;
      }

      const chatroom = state.chats[`${action.chatId}`];
      const pinnedMessages = { ...chatroom.pinnedMessages };
      delete pinnedMessages[`${action.messageId}`];

      const pinnedOrder = (chatroom.pinnedOrder || []).filter((id) => id !== action.messageId);

      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...chatroom,
            pinnedMessages,
            pinnedOrder,
          },
        },
      };
    }

    case 'ChatList/RECEIVE_PINNED_MESSAGES': {
      const chatroom = state.chats[`${action.chatId}`];

      const pinnedOrder = action.pinnedMessagesMetadata.map((pm) => pm.id);

      const pinnedMessages: PinnedMessageMap = {};
      action.pinnedMessagesMetadata.forEach((metadata) => {
        pinnedMessages[`${metadata.id}`] = {
          id: metadata.id,
          pinOrder: metadata.pinOrder,
          pinnedBy: metadata.pinnedBy,
          pinnedTime: metadata.pinnedTime,
          message: {
            id: metadata.id,
            senderId: metadata.senderId,
            senderName: metadata.senderName || '',
            sentTime: '',
            messageType: metadata.messageType,
            textContent: metadata.textContent || metadata.displayName,
            dataContent: null,
            displayName: metadata.displayName,
            fileName: metadata.fileName,
            localPath: undefined,
            status: undefined,
            seenByCount: 0,
            seenByCurrentUser: false,
            acknowledgements: {},
            mentionedUsers: [],
            mentionedNames: [],
            redactTime: undefined,
            replyToMessageId: undefined,
            ack: {},
            highPriority: false,
          } as ChatroomMessage,
        };
      });

      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...chatroom,
            pinnedOrder,
            pinnedMessages,
          },
        },
      };
    }

    case 'ChatList/REDACT_SUCCESS':
      if (state.activeChatId !== action.chatId) {
        return state;
      }

      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            messages: {
              ...state.chats[`${action.chatId}`].messages,

              [`${action.messageId}`]: {
                ...state.chats[`${action.chatId}`].messages[`${action.messageId}`],

                redactTime: action.redactTime,
              },
            },
          },
        },
      };

    case 'ChatList/UNREDACT_SUCCESS':
      if (state.activeChatId !== action.chatId) {
        return state;
      }

      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            messages: {
              ...state.chats[`${action.chatId}`].messages,

              [`${action.messageId}`]: {
                ...state.chats[`${action.chatId}`].messages[`${action.messageId}`],

                redactTime: undefined,
              },
            },
          },
        },
      };

    case 'ChatList/EDIT_SUCCESS': {
      if (state.activeChatId !== action.chatId) {
        return state;
      }

      const chat = state.chats[`${action.chatId}`];
      const messageOrder = chat.messageOrder ?? [];
      const isNewestMessage =
        messageOrder.length > 0 && `${messageOrder[messageOrder.length - 1]}` === `${action.messageId}`;

      // A bookmarked message is stored as its own copy under pinnedMessages, and the bookmarks
      // panel renders that copy rather than the one in `messages`. Without this the panel keeps
      // showing the pre-edit text until the pinned messages are refetched.
      const pinned = chat.pinnedMessages?.[`${action.messageId}`];
      const pinnedMessages = pinned?.message
        ? {
            ...chat.pinnedMessages,

            [`${action.messageId}`]: {
              ...pinned,

              message: {
                ...pinned.message,

                textContent: action.textContent,
                editTime: action.editTime,
                editedBy: action.editedBy,
              },
            },
          }
        : chat.pinnedMessages;

      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...chat,

            // Editing the newest message refreshes the chat list preview, but an edit is not new
            // activity - the room is deliberately not re-sorted and no unread badge is raised.
            lastMessage:
              isNewestMessage && chat.lastMessage
                ? { ...chat.lastMessage, textContent: action.textContent }
                : chat.lastMessage,

            pinnedMessages,

            messages: {
              ...chat.messages,

              [`${action.messageId}`]: {
                ...chat.messages[`${action.messageId}`],

                textContent: action.textContent,
                editTime: action.editTime,
                editedBy: action.editedBy,
              },
            },
          },
        },
      };
    }

    case 'ChatList/DELETE_FILE_MESSAGE_SUCCESS': {
      const chat = state.chats[`${action.chatId}`];

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [`${action.messageId}`]: deletedMessage, ...messages } = chat.messages;

      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            messages: {
              ...messages,
            },

            messageOrder: chat.messageOrder.filter((msgId) => msgId !== action.messageId),
            totalMessages: chat.totalMessages - 1,
          },
        },
      };
    }

    case 'ChatList/SET_CLEARED_COUNT': {
      return {
        ...state,

        chats: {
          ...state.chats,

          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],

            clearedCount:
              action.newCount !== undefined
                ? (state.chats[`${action.chatId}`] || {}).clearedCount + action.newCount
                : (state.chats[`${action.chatId}`] || {}).clearedCount,
          },
        },
      };
    }

    case 'ChatList/SEARCH_RESULTS': {
      return {
        ...state,

        searchResults: action.searchResults,
        deepSearchFilter: state.filter,
      };
    }

    case 'ChatList/SET_SELECTED_RESULT': {
      return {
        ...state,

        selectedResult: action.selectedResult,
        scrolledToSelectedResult: false,
      };
    }

    case 'ChatList/SET_SELECTED_SCROLL_TARGET': {
      return {
        ...state,
        selectedScrollTargetId: action.selectedScrollTargetId,
        scrolledToScrollTarget: false,
      };
    }

    case 'ChatList/SET_SCROLLED_TO_SCROLL_TARGET': {
      return {
        ...state,
        scrolledToScrollTarget: action.scrolledToScrollTarget,
      };
    }

    case 'ChatList/SET_SCROLLED_TO_SEARCH_RESULT': {
      return {
        ...state,
        scrolledToSelectedResult: action.scrolledToSelectedResult,
      };
    }

    case 'ChatList/RECEIVE_ROOM_DO_NOT_DISTURB': {
      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],
            doNotDisturb: action.doNotDisturb,
          },
        },
      };
    }

    case 'ChatList/RECEIVE_ADD_CHATROOM_MANAGER': {
      const chat = state.chats[`${action.chatId}`];
      if (!chat) {
        return state;
      }

      const managerIds = chat.managerIds ? [...chat.managerIds] : [];
      // Add the user to manager Ids if it isn't already there
      // otherwise, nothing to do so just return state
      if (!managerIds.includes(action.userId)) {
        managerIds.push(action.userId);
      } else {
        return state;
      }

      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],
            managerIds,
          },
        },
      };
    }

    case 'ChatList/RECEIVE_REMOVE_CHATROOM_MANAGER': {
      const chat = state.chats[`${action.chatId}`];
      if (!chat) {
        return state;
      }

      const managerIds = chat.managerIds || [];
      let newManagerIds: number[] = [];
      // Remove the user from manager Ids if the id there
      // otherwise, nothing to do so just return state
      if (managerIds.includes(action.userId)) {
        newManagerIds = managerIds.filter((id) => id !== action.userId);
      } else {
        return state;
      }
      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],
            managerIds: newManagerIds,
          },
        },
      };
    }

    case 'ChatList/RECEIVE_CHATROOM_MANAGERS': {
      const chat = state.chats[`${action.chatId}`];
      if (!chat) {
        return state;
      }

      return {
        ...state,
        chats: {
          ...state.chats,
          [`${action.chatId}`]: {
            ...state.chats[`${action.chatId}`],
            managerIds: action.managerIds,
          },
        },
      };
    }

    default:
      return state;
  }
};

export default reducer as Reducer<ChatListState>;
